import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { isNotNil } from 'ramda'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { pLimitFiveService } from 'src/v3/utilities/pLimitService'

import {
  AccreditationV2,
  ANCCAccreditation,
  CAPCEAccreditationV2,
  CategoryResponse,
  CEBrokerAccreditationV2,
  Event,
  EventAccreditation,
  EventCERecord,
  EventCompletionEvent,
  EventCompliance,
  EventInvite,
  EventSession,
  Instructor,
  LinkedService,
  NewJerseyAccreditationV2,
  NewYorkAccreditationV2,
  AccreditedAccreditationV2,
  NotAccreditedV2,
  QuestionData,
  QuestionSessionData,
  QuizSessionAnswer,
  QuizSessionResult,
  QuizV2,
  Reference,
  Syllabus,
  Template,
  TimeStamps,
  User,
  VirginiaAccreditationV2,
  EventTypeName,
  AccreditationType,
} from 'src/v3/core/types'
import { AccreditationsByType } from './accreditations'
import { SessionCapacityFull as SessionCapacity } from './types'

export type HydratedSession = EventSession & {
  // Managers get every invite; everyone else gets only their own
  invites: Pick<EventInvite, 'id' | 'userID'>[]
  // Seats held (Accepted/Attended) regardless of viewer; use this for spots open
  seatsTaken?: number
  capacity?: number
}

export type EventTemplate = Template

export type EventTemplateAssociation = {
  id: string
  eventID: string
  templateID: string
} & TimeStamps

type ResultWithAnswers = QuizSessionResult & {
  answers: QuizSessionAnswer[]
}

export type UserGroup = {
  id?: string
  name: string
  linkedServiceID: string
  users?: string[]
  createdTime?: string
  updatedTime?: string
}

export let fetchEvents = createAsyncThunk('events/fetchEventsStatus', () =>
  axios
    .get<any[]>('/api/events')
    .then(pluckData)
    .then((data) =>
      data.map((event): Event => {
        let [eventType, location] = Object.entries<any>(event.eventType)[0]
        let flattenedLocation =
          eventType === 'hybrid' &&
          Array.isArray(location) &&
          location.length === 2
            ? { ...location[1], ...location[0], virtualName: location[1].name }
            : location
        return {
          ...event,
          attendanceTrackingMechanism: {
            kind: isNotNil(event.attendanceTrackingMechanism.quizPasscode)
              ? 'quizPasscode'
              : 'managerTracked',
            quizPasscode: event.attendanceTrackingMechanism.quizPasscode,
          },
          category: {
            kind: event.category.other ? 'other' : event.category,
            other: event.category.other,
          },
          categoryName: isNotNil(event.category.other)
            ? 'Other'
            : {
                'airway|Respiratory|Ventilation':
                  'Airway|Respiratory|Ventilation',
                medical: 'Medical',
                trauma: 'Trauma',
                operations: 'Operations',
                'critical Care': 'Critical Care',
                cardiovascular: 'Cardiovascular',
                pediatrics: 'Pediatrics',
              }[event.category as string],
          location: {
            kind: eventType,
            ...flattenedLocation,
          },
          eventType:
            {
              virtual: 'Virtual',
              inPerson: 'In Person',
              hybrid: 'Hybrid',
            }[eventType] || 'Unknown',
        }
      }),
    ),
)

export let fetchSessions = createAsyncThunk('events/fetchSessionsStatus', () =>
  pLimitFiveService(() =>
    axios.get<HydratedSession[]>('/api/v2/event/sessions').then(pluckData),
  ),
)

export let fetchCapacity = createAsyncThunk(
  'events/fetchCapacityStatus',
  (sessionID: string) =>
    pLimitFiveService(() =>
      axios
        .get<SessionCapacity>(`/api/event/session/${sessionID}/capacity`)
        .then(pluckData),
    ),
)

export let fetchInvites = createAsyncThunk(
  'events/fetchInvitesStatus',
  (sessionID: string) =>
    pLimitFiveService(() =>
      axios
        .get<EventInvite[]>(`/api/event/invites?sessionID=${sessionID}`)
        .then(pluckData),
    ),
)

export let fetchInstructors = createAsyncThunk(
  'events/fetchInstructorsStatus',
  () =>
    pLimitFiveService(() =>
      axios.get<Instructor[]>('/api/event/instructors').then(pluckData),
    ),
)

export let fetchAccreditations = createAsyncThunk(
  'events/fetchAccreditationsStatus',
  () =>
    pLimitFiveService(() =>
      axios
        .get<{ [kind: string]: AccreditationV2 }[]>('/api/event/accreditations')
        .then(pluckData)
        .then((data) =>
          data.map((a): AccreditationV2 => {
            let [kind, obj] = Object.entries(a)[0]
            return { ...obj, kind: kind as any }
          }),
        ),
    ),
)

export let fetchEventAccreditations = createAsyncThunk(
  'events/fetchEventAccreditationsStatus',
  () =>
    pLimitFiveService(() =>
      axios
        .get<EventAccreditation[]>('/api/event/accreditations/joins')
        .then(pluckData),
    ),
)

export let fetchEventCompletionEvents = createAsyncThunk(
  'events/fetchEventCompletionEventsStatus',
  (eventID: string) =>
    pLimitFiveService(() =>
      axios
        .get<EventCompletionEvent[]>(
          `/api/event/completion-events?eventID=${eventID}`,
        )
        .then(pluckData),
    ),
)

export let fetchEventCompletionEventsBySession = createAsyncThunk(
  'events/fetchEventCompletionEventsBySessionStatus',
  (sessionID: string) =>
    pLimitFiveService(() =>
      axios
        .get<EventCompletionEvent[]>(
          `/api/event/completion-events?sessionID=${sessionID}`,
        )
        .then(pluckData),
    ),
)

export let fetchEventCompliances = createAsyncThunk(
  'events/fetchEventComplianceStatus',
  () =>
    pLimitFiveService(() =>
      axios
        .get<EventCompliance[]>('/api/event/compliances')
        .then(pluckData)
        .then((data) =>
          data.reduce((obj, ec) => ({ ...obj, [ec.eventID]: ec }), {}),
        ),
    ),
)

export let fetchCategories = createAsyncThunk(
  'events/fetchCategoriesStatus',
  () =>
    pLimitFiveService(() =>
      axios.get<CategoryResponse>('/api/event/categories').then(pluckData),
    ),
)

export let fetchUsers = createAsyncThunk(
  'events/fetchUsersStatus',
  (linkedServiceID: string) =>
    pLimitFiveService(() =>
      axios
        .get<User[]>(`/api/event/users?linkedServiceID=${linkedServiceID}`)
        .then(pluckData),
    ),
)

export let fetchUsersByEvent = createAsyncThunk(
  'events/fetchUsersByEventStatus',
  (eventID: string) =>
    pLimitFiveService(() =>
      axios.get<User[]>(`/api/event/users?eventID=${eventID}`).then(pluckData),
    ),
)

export let fetchUserGroups = createAsyncThunk(
  'events/fetchUserGroupsStatus',
  (linkedServiceID: string) =>
    pLimitFiveService(() =>
      axios
        .get<UserGroup[]>(
          `/api/event/user-groups?linkedServiceID=${linkedServiceID}`,
        )
        .then(pluckData),
    ),
)

export let fetchLinkedServices = createAsyncThunk(
  'events/fetchLinkedServicesStatus',
  () =>
    pLimitFiveService(() =>
      axios.get<LinkedService[]>('/api/event/linked-services').then(pluckData),
    ),
)

export let fetchQuiz = createAsyncThunk(
  'events/fetchQuizStatus',
  (eventID: string) =>
    pLimitFiveService(() =>
      axios.get<QuizV2>(`/api/event/quiz?eventID=${eventID}`).then(pluckData),
    ),
)

export let fetchQuizQuestions = createAsyncThunk(
  'events/fetchQuizQuestionsStatus',
  (quizID: string) =>
    pLimitFiveService(() =>
      axios
        .get<QuestionData[]>(`/api/event/quiz/questions?quizID=${quizID}`)
        .then(pluckData),
    ),
)

export let fetchQuizSessionQuestions = createAsyncThunk(
  'events/fetchQuizSessionQuestionsStatus',
  (quizID: string) =>
    pLimitFiveService(() =>
      axios
        .get<QuestionSessionData[]>(
          `/api/event/quiz/session/questions?quizID=${quizID}`,
        )
        .then(pluckData),
    ),
)

export let fetchQuizSessionAnswers = createAsyncThunk(
  'events/fetchQuizSessionAnswersStatus',
  (quizID: string) =>
    pLimitFiveService(() =>
      axios
        .get<QuizSessionAnswer[]>(
          `/api/event/quiz/session/answers?quizID=${quizID}`,
        )
        .then(pluckData),
    ),
)

export let fetchQuizSessionResult = createAsyncThunk(
  'events/fetchQuizSessionResultStatus',
  (eventID: string) =>
    pLimitFiveService(() =>
      axios
        .get<ResultWithAnswers>(
          `/api/event/quiz/session/result?eventID=${eventID}`,
        )
        .then(pluckData),
    ),
)

export let fetchEventCERecords = createAsyncThunk(
  'events/fetchEventCERecordsStatus',
  (eventID: string) =>
    pLimitFiveService(() =>
      axios
        .get<EventCERecord[]>(`/api/event/event-ce-records?eventID=${eventID}`)
        .then(pluckData),
    ),
)

export let fetchEventTemplates = createAsyncThunk(
  'events/fetchEventTemplatesStatus',
  () =>
    pLimitFiveService(() =>
      axios.get<EventTemplate[]>('/api/event/templates').then(pluckData),
    ),
)

export let createEventTemplate = createAsyncThunk(
  'events/createEventTemplateStatus',
  async (
    template: Omit<EventTemplate, 'id' | 'createdTime' | 'updatedTime'>,
    { dispatch },
  ) => {
    let result = await axios
      .post<EventTemplate>('/api/event/template', template)
      .then(pluckData)
    dispatch(fetchEventTemplates())
    return result
  },
)

export let updateEventTemplate = createAsyncThunk(
  'events/updateEventTemplateStatus',
  (template: EventTemplate) =>
    axios.put<EventTemplate>('/api/event/template', template).then(pluckData),
)

export let deleteEventTemplate = createAsyncThunk(
  'events/deleteEventTemplateStatus',
  (templateId: string) =>
    axios.delete(`/api/event/template/${templateId}`).then(() => templateId),
)

export let fetchEventTemplateAssociations = createAsyncThunk(
  'events/fetchEventTemplateAssociationsStatus',
  () =>
    pLimitFiveService(() =>
      axios
        .get<EventTemplateAssociation[]>('/api/event/template-associations')
        .then(pluckData),
    ),
)

export let createEventTemplateAssociation = createAsyncThunk(
  'events/createEventTemplateAssociationStatus',
  (association: { eventId: string; templateId: string }) =>
    axios
      .post<EventTemplateAssociation>('/api/event/template-association', {
        eventID: association.eventId,
        templateID: association.templateId,
      })
      .then(pluckData),
)

export let deleteEventTemplateAssociation = createAsyncThunk(
  'events/deleteEventTemplateAssociationStatus',
  (associationId: string) =>
    axios
      .delete(`/api/event/template-association/${associationId}`)
      .then(() => associationId),
)

export let fetchSyllabus = createAsyncThunk(
  'events/fetchSyllabusStatus',
  (eventID: string) =>
    pLimitFiveService(() =>
      axios
        .get<Syllabus>(`/api/event/syllabus?eventID=${eventID}`)
        .then(pluckData),
    ),
)

export let fetchReferences = createAsyncThunk(
  'events/fetchReferencesStatus',
  () =>
    pLimitFiveService(() =>
      axios.get<Reference[]>('/api/event/syllabus/references').then(pluckData),
    ),
)

type EntityStatus = string | 'unselected'

type EventsState = {
  activeEvent: EntityStatus
  foamfratFilter: boolean
  eventTypeFilter: EventTypeName | 'All'
  dateLowerFilter: string
  dateUpperFilter: string
  accreditationFilter: AccreditationType | 'All'
  events: Event[]
  eventsStatus: Status
  sessions: HydratedSession[]
  sessionsStatus: Status
  capacity: Record<string, SessionCapacity>
  capacityStatus: Record<string, Status>
  invites: Record<string, EventInvite[]>
  invitesStatus: Record<string, Status>
  instructors: Instructor[]
  instructorsStatus: Status
  accreditations: AccreditationV2[]
  accreditationsStatus: Status
  eventAccreditations: EventAccreditation[]
  eventAccreditationsStatus: Status
  eventCompletionEvents: Record<string, EventCompletionEvent[]>
  eventCompletionEventsStatus: Record<string, Status>
  eventCompliances: Record<string, EventCompliance>
  eventCompliancesStatus: Status
  categories: CategoryResponse
  categoriesStatus: Status
  users: Record<string, User[]>
  usersStatus: Record<string, Status>
  userGroups: Record<string, UserGroup[]>
  userGroupsStatus: Record<string, Status>
  linkedServices: LinkedService[]
  linkedServicesStatus: Status
  quizzes: Record<string, QuizV2>
  quizzesStatus: Record<string, Status>
  quizQuestions: Record<string, QuestionData[]>
  quizQuestionsStatus: Record<string, Status>
  quizSessionQuestions: Record<string, QuestionSessionData[]>
  quizSessionQuestionsStatus: Record<string, Status>
  // The requestId of the most recently DISPATCHED fetchQuizSessionQuestions per quiz. Only it
  // may settle the status or replace the questions, so an overlapping straggler (e.g.
  // StrictMode's double mount) can't overwrite a fresher session's isCurrent flags or wedge
  // useFreshFetch. Same rule as latestProgressRequestID in events-v2.slice.ts.
  latestSessionQuestionsRequestID: Record<string, string>
  quizSessionAnswers: Record<string, QuizSessionAnswer[]>
  quizSessionAnswersStatus: Record<string, Status>
  quizSessionResults: Record<string, ResultWithAnswers>
  quizSessionResultsStatus: Record<string, Status>
  eventCERecords: Record<string, EventCERecord[]>
  eventCERecordsStatus: Record<string, Status>
  templates: EventTemplate[]
  templatesStatus: Status
  templateAssociations: EventTemplateAssociation[]
  templateAssociationsStatus: Status
  syllabi: Record<string, Syllabus>
  syllabiStatus: Record<string, Status>
  references: Reference[]
  referencesStatus: Status
}

export let initialState: EventsState = {
  activeEvent: 'unselected',
  foamfratFilter: true,
  eventTypeFilter: 'All',
  dateLowerFilter: dayjs().startOf('day').format(),
  dateUpperFilter: dayjs().add(1, 'year').format(),
  accreditationFilter: 'All',
  events: [],
  eventsStatus: 'uninitialized',
  sessions: [],
  sessionsStatus: 'uninitialized',
  capacity: {},
  capacityStatus: {},
  invites: {},
  invitesStatus: {},
  instructors: [],
  instructorsStatus: 'uninitialized',
  accreditations: [],
  accreditationsStatus: 'uninitialized',
  eventAccreditations: [],
  eventAccreditationsStatus: 'uninitialized',
  eventCompletionEvents: {},
  eventCompletionEventsStatus: {},
  eventCompliances: {},
  eventCompliancesStatus: 'uninitialized',
  categories: { standard: [], custom: [] },
  categoriesStatus: 'uninitialized',
  users: {},
  usersStatus: {},
  userGroups: {},
  userGroupsStatus: {},
  linkedServices: [],
  linkedServicesStatus: 'uninitialized',
  quizzes: {},
  quizzesStatus: {},
  quizQuestions: {},
  quizQuestionsStatus: {},
  quizSessionQuestions: {},
  quizSessionQuestionsStatus: {},
  latestSessionQuestionsRequestID: {},
  quizSessionAnswers: {},
  quizSessionAnswersStatus: {},
  quizSessionResults: {},
  quizSessionResultsStatus: {},
  eventCERecords: {},
  eventCERecordsStatus: {},
  templates: [],
  templatesStatus: 'uninitialized',
  templateAssociations: [],
  templateAssociationsStatus: 'uninitialized',
  syllabi: {},
  syllabiStatus: {},
  references: [],
  referencesStatus: 'uninitialized',
}

export let eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    activeEventChanged: (state, action: PayloadAction<EntityStatus>) => {
      state.activeEvent = action.payload
    },
    foamfratFilterChanged: (state, action: PayloadAction<boolean>) => {
      state.foamfratFilter = action.payload
    },
    eventTypeFilterChanged: (
      state,
      action: PayloadAction<EventTypeName | 'All'>,
    ) => {
      state.eventTypeFilter = action.payload
    },
    dateLowerFilterChanged: (state, action: PayloadAction<string>) => {
      state.dateLowerFilter = action.payload
    },
    dateUpperFilterChanged: (state, action: PayloadAction<string>) => {
      state.dateUpperFilter = action.payload
    },
    accreditationFilterChanged: (
      state,
      action: PayloadAction<AccreditationType | 'All'>,
    ) => {
      state.accreditationFilter = action.payload
    },
    sessionsChanged: (state, action: PayloadAction<HydratedSession[]>) => {
      state.sessions = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.eventsStatus = 'pending'
      })
      .addCase(fetchEvents.rejected, (state) => {
        state.eventsStatus = 'rejected'
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.eventsStatus = 'fulfilled'
        state.events = action.payload
      })
      .addCase(fetchSessions.pending, (state) => {
        state.sessionsStatus = 'pending'
      })
      .addCase(fetchSessions.rejected, (state) => {
        state.sessionsStatus = 'rejected'
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.sessionsStatus = 'fulfilled'
        state.sessions = action.payload
      })
      .addCase(fetchCapacity.pending, (state, action) => {
        state.capacityStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCapacity.rejected, (state, action) => {
        state.capacityStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCapacity.fulfilled, (state, action) => {
        state.capacityStatus[action.meta.arg] = 'fulfilled'
        state.capacity[action.meta.arg] = action.payload
      })
      .addCase(fetchInvites.pending, (state, action) => {
        state.invitesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchInvites.rejected, (state, action) => {
        state.invitesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchInvites.fulfilled, (state, action) => {
        state.invitesStatus[action.meta.arg] = 'fulfilled'
        state.invites[action.meta.arg] = action.payload
      })
      .addCase(fetchInstructors.pending, (state) => {
        state.instructorsStatus = 'pending'
      })
      .addCase(fetchInstructors.rejected, (state) => {
        state.instructorsStatus = 'rejected'
      })
      .addCase(fetchInstructors.fulfilled, (state, action) => {
        state.instructorsStatus = 'fulfilled'
        state.instructors = action.payload
      })
      .addCase(fetchAccreditations.pending, (state) => {
        state.accreditationsStatus = 'pending'
      })
      .addCase(fetchAccreditations.rejected, (state) => {
        state.accreditationsStatus = 'rejected'
      })
      .addCase(fetchAccreditations.fulfilled, (state, action) => {
        state.accreditationsStatus = 'fulfilled'
        state.accreditations = action.payload
      })
      .addCase(fetchEventAccreditations.pending, (state) => {
        state.eventAccreditationsStatus = 'pending'
      })
      .addCase(fetchEventAccreditations.rejected, (state) => {
        state.eventAccreditationsStatus = 'rejected'
      })
      .addCase(fetchEventAccreditations.fulfilled, (state, action) => {
        state.eventAccreditationsStatus = 'fulfilled'
        state.eventAccreditations = action.payload
      })
      .addCase(fetchEventCompletionEvents.pending, (state, action) => {
        state.eventCompletionEventsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchEventCompletionEvents.rejected, (state, action) => {
        state.eventCompletionEventsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEventCompletionEvents.fulfilled, (state, action) => {
        state.eventCompletionEventsStatus[action.meta.arg] = 'fulfilled'
        state.eventCompletionEvents[action.meta.arg] = action.payload
      })
      .addCase(fetchEventCompletionEventsBySession.pending, (state, action) => {
        state.eventCompletionEventsStatus[action.meta.arg] = 'pending'
      })
      .addCase(
        fetchEventCompletionEventsBySession.rejected,
        (state, action) => {
          state.eventCompletionEventsStatus[action.meta.arg] = 'rejected'
        },
      )
      .addCase(
        fetchEventCompletionEventsBySession.fulfilled,
        (state, action) => {
          state.eventCompletionEventsStatus[action.meta.arg] = 'fulfilled'
          state.eventCompletionEvents[action.meta.arg] = action.payload
        },
      )
      .addCase(fetchEventCompliances.pending, (state) => {
        state.eventCompliancesStatus = 'pending'
      })
      .addCase(fetchEventCompliances.rejected, (state) => {
        state.eventCompliancesStatus = 'rejected'
      })
      .addCase(fetchEventCompliances.fulfilled, (state, action) => {
        state.eventCompliancesStatus = 'fulfilled'
        state.eventCompliances = action.payload
      })
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesStatus = 'pending'
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.categoriesStatus = 'rejected'
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesStatus = 'fulfilled'
        state.categories = action.payload
      })
      .addCase(fetchUsers.pending, (state, action) => {
        state.usersStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersStatus[action.meta.arg] = 'fulfilled'
        state.users[action.meta.arg] = action.payload
      })
      .addCase(fetchUsersByEvent.pending, (state, action) => {
        state.usersStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchUsersByEvent.rejected, (state, action) => {
        state.usersStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchUsersByEvent.fulfilled, (state, action) => {
        state.usersStatus[action.meta.arg] = 'fulfilled'
        state.users[action.meta.arg] = action.payload
      })
      .addCase(fetchUserGroups.pending, (state, action) => {
        state.userGroupsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchUserGroups.rejected, (state, action) => {
        state.userGroupsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchUserGroups.fulfilled, (state, action) => {
        state.userGroupsStatus[action.meta.arg] = 'fulfilled'
        state.userGroups[action.meta.arg] = action.payload
      })
      .addCase(fetchLinkedServices.pending, (state) => {
        state.linkedServicesStatus = 'pending'
      })
      .addCase(fetchLinkedServices.rejected, (state) => {
        state.linkedServicesStatus = 'rejected'
      })
      .addCase(fetchLinkedServices.fulfilled, (state, action) => {
        state.linkedServicesStatus = 'fulfilled'
        state.linkedServices = action.payload
      })
      .addCase(fetchQuiz.pending, (state, action) => {
        state.quizzesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchQuiz.rejected, (state, action) => {
        state.quizzesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchQuiz.fulfilled, (state, action) => {
        state.quizzesStatus[action.meta.arg] = 'fulfilled'
        state.quizzes[action.meta.arg] = action.payload
      })
      .addCase(fetchQuizQuestions.pending, (state, action) => {
        state.quizQuestionsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchQuizQuestions.rejected, (state, action) => {
        state.quizQuestionsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchQuizQuestions.fulfilled, (state, action) => {
        state.quizQuestionsStatus[action.meta.arg] = 'fulfilled'
        state.quizQuestions[action.meta.arg] = action.payload
      })
      .addCase(fetchQuizSessionQuestions.pending, (state, action) => {
        state.quizSessionQuestionsStatus[action.meta.arg] = 'pending'
        state.latestSessionQuestionsRequestID[action.meta.arg] =
          action.meta.requestId
      })
      .addCase(fetchQuizSessionQuestions.rejected, (state, action) => {
        // Superseded request: see latestSessionQuestionsRequestID.
        if (
          state.latestSessionQuestionsRequestID[action.meta.arg] !==
          action.meta.requestId
        ) {
          return
        }
        state.quizSessionQuestionsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchQuizSessionQuestions.fulfilled, (state, action) => {
        // Superseded request: see latestSessionQuestionsRequestID.
        if (
          state.latestSessionQuestionsRequestID[action.meta.arg] !==
          action.meta.requestId
        ) {
          return
        }
        state.quizSessionQuestionsStatus[action.meta.arg] = 'fulfilled'
        state.quizSessionQuestions[action.meta.arg] = action.payload
      })
      .addCase(fetchQuizSessionAnswers.pending, (state, action) => {
        state.quizSessionAnswersStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchQuizSessionAnswers.rejected, (state, action) => {
        state.quizSessionAnswersStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchQuizSessionAnswers.fulfilled, (state, action) => {
        state.quizSessionAnswersStatus[action.meta.arg] = 'fulfilled'
        state.quizSessionAnswers[action.meta.arg] = action.payload
      })
      .addCase(fetchQuizSessionResult.pending, (state, action) => {
        state.quizSessionResultsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchQuizSessionResult.rejected, (state, action) => {
        state.quizSessionResultsStatus[action.meta.arg] = 'rejected'
        // eslint-disable-next-line fp/no-delete
        delete state.quizSessionResults[action.meta.arg]
      })
      .addCase(fetchQuizSessionResult.fulfilled, (state, action) => {
        state.quizSessionResultsStatus[action.meta.arg] = 'fulfilled'
        state.quizSessionResults[action.meta.arg] = action.payload
      })
      .addCase(fetchEventCERecords.pending, (state, action) => {
        state.eventCERecordsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchEventCERecords.rejected, (state, action) => {
        state.eventCERecordsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEventCERecords.fulfilled, (state, action) => {
        state.eventCERecordsStatus[action.meta.arg] = 'fulfilled'
        state.eventCERecords[action.meta.arg] = action.payload
      })
      .addCase(fetchEventTemplates.pending, (state) => {
        state.templatesStatus = 'pending'
      })
      .addCase(fetchEventTemplates.rejected, (state) => {
        state.templatesStatus = 'rejected'
      })
      .addCase(fetchEventTemplates.fulfilled, (state, action) => {
        state.templatesStatus = 'fulfilled'
        state.templates = action.payload
      })
      .addCase(createEventTemplate.fulfilled, (state, action) => {
        state.templates = [...state.templates, action.payload]
      })
      .addCase(updateEventTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.map((t) =>
          t.id === action.payload.id ? action.payload : t,
        )
      })
      .addCase(deleteEventTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter((t) => t.id !== action.payload)
      })
      .addCase(fetchEventTemplateAssociations.pending, (state) => {
        state.templateAssociationsStatus = 'pending'
      })
      .addCase(fetchEventTemplateAssociations.rejected, (state) => {
        state.templateAssociationsStatus = 'rejected'
      })
      .addCase(fetchEventTemplateAssociations.fulfilled, (state, action) => {
        state.templateAssociationsStatus = 'fulfilled'
        state.templateAssociations = action.payload
      })
      .addCase(createEventTemplateAssociation.fulfilled, (state, action) => {
        state.templateAssociations = [
          ...state.templateAssociations,
          action.payload,
        ]
      })
      .addCase(deleteEventTemplateAssociation.fulfilled, (state, action) => {
        state.templateAssociations = state.templateAssociations.filter(
          (a) => a.id !== action.payload,
        )
      })
      .addCase(fetchSyllabus.pending, (state, action) => {
        state.syllabiStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchSyllabus.rejected, (state, action) => {
        state.syllabiStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchSyllabus.fulfilled, (state, action) => {
        state.syllabiStatus[action.meta.arg] = 'fulfilled'
        state.syllabi[action.meta.arg] = action.payload
      })
      .addCase(fetchReferences.pending, (state) => {
        state.referencesStatus = 'pending'
      })
      .addCase(fetchReferences.rejected, (state) => {
        state.referencesStatus = 'rejected'
      })
      .addCase(fetchReferences.fulfilled, (state, action) => {
        state.referencesStatus = 'fulfilled'
        state.references = action.payload
      })
  },
})

export let {
  activeEventChanged,
  foamfratFilterChanged,
  eventTypeFilterChanged,
  dateLowerFilterChanged,
  dateUpperFilterChanged,
  accreditationFilterChanged,
  sessionsChanged,
} = eventsSlice.actions

export let selectActiveEventID = (state: AppRootState) =>
  state.events.activeEvent
export let selectActiveEvent = (state: AppRootState) =>
  state.events.events.find((e) => e.id === state.events.activeEvent)

export let selectFoamfratFilter = (state: AppRootState) =>
  state.events.foamfratFilter
export let selectEventTypeFilter = (state: AppRootState) =>
  state.events.eventTypeFilter
export let selectDateLowerFilter = (state: AppRootState) =>
  state.events.dateLowerFilter
export let selectDateUpperFilter = (state: AppRootState) =>
  state.events.dateUpperFilter
export let selectAccreditationFilter = (state: AppRootState) =>
  state.events.accreditationFilter

export let selectEventById = (id: string) => (state: AppRootState) =>
  state.events.events.find((e) => e.id === id)
export let selectEvents = (state: AppRootState) => state.events.events
export let selectEventsStatus = (state: AppRootState) =>
  state.events.eventsStatus

export let selectSessions = (state: AppRootState) => state.events.sessions
export let selectSessionsByID = (id: string) => (state: AppRootState) =>
  state.events.sessions.find((s) => s.id === id)
export let selectSessionsByEventID = (id: string) => (state: AppRootState) =>
  // eslint-disable-next-line fp/no-mutating-methods
  state.events.sessions
    .filter((s) => s.eventID === id)
    .sort((a, b) => dayjs(a.sessionTime).diff(b.sessionTime))
    .map((s, i) => ({
      ...s,
      name: s.name || `Session ${i + 1}`,
      endTime:
        s.endTime ||
        dayjs
          .utc(s.sessionTime)
          .add(selectEventById(id)(state)?.hours || 1, 'hours')
          .format(),
    }))
export let selectSessionsStatus = (state: AppRootState) =>
  state.events.sessionsStatus

export let selectSessionCapacity = (state: AppRootState) =>
  state.events.capacity
export let selectSessionCapacityStatus = (state: AppRootState) =>
  state.events.capacityStatus

export let selectInvites = (state: AppRootState) => state.events.invites
export let selectInvitesStatus = (state: AppRootState) =>
  state.events.invitesStatus

export let selectInstructors = (state: AppRootState) => state.events.instructors
export let selectInstructorsByLSID = (id: string) => (state: AppRootState) =>
  state.events.instructors.filter(
    (i) =>
      (id === 'FOAMfrat' && !i.linkedServiceID) || id === i.linkedServiceID,
  )
export let selectEventInstructorNames = (state: AppRootState) =>
  state.events.events.reduce(
    (dict, event) => {
      dict[event.id] =
        event.instructorIDs
          .map((id) => state.events.instructors.find((i) => i.id === id)?.name)
          ?.join(', ') || ''
      return dict
    },
    {} as Record<string, string>,
  )
export let selectInstructorsStatus = (state: AppRootState) =>
  state.events.instructorsStatus

export let selectAccreditations = (state: AppRootState) =>
  state.events.accreditations
export let selectAccreditationsStatus = (state: AppRootState) =>
  state.events.accreditationsStatus

let groupAccreditationsByType = (accreditations: AccreditationV2[]) => {
  let all: AccreditationsByType = {
    ancc: accreditations.filter(
      (a) => a.kind === 'ancc',
    ) as ANCCAccreditation[],
    capce: accreditations.filter(
      (a) => a.kind === 'capce',
    ) as CAPCEAccreditationV2[],
    newJersey: accreditations.filter(
      (a) => a.kind === 'newJersey',
    ) as NewJerseyAccreditationV2[],
    ceBroker: accreditations.filter(
      (a) => a.kind === 'ceBroker',
    ) as CEBrokerAccreditationV2[],
    newYork: accreditations.filter(
      (a) => a.kind === 'newYork',
    ) as NewYorkAccreditationV2[],
    virginia: accreditations.filter(
      (a) => a.kind === 'virginia',
    ) as VirginiaAccreditationV2[],
    accredited: accreditations.filter(
      (a) => a.kind === 'accredited',
    ) as AccreditedAccreditationV2[],
    notAccredited: accreditations.filter(
      (a) => a.kind === 'notAccredited',
    ) as NotAccreditedV2[],
  }
  return all
}
export let selectAccreditationsByType = (state: AppRootState) =>
  groupAccreditationsByType(state.events.accreditations)
export let selectAccreditationsByTypeByEvent =
  (eventID: string) => (state: AppRootState) =>
    groupAccreditationsByType(
      state.events.accreditations.filter((a) =>
        state.events.eventAccreditations.some(
          (ea) => ea.eventID === eventID && ea.accreditationID === a.id,
        ),
      ),
    )

export let selectEventAccreditations = (state: AppRootState) =>
  state.events.eventAccreditations
export let selectEventAccreditationsStatus = (state: AppRootState) =>
  state.events.eventAccreditationsStatus

export let selectCompletionEventsByID = (id: string) => (state: AppRootState) =>
  state.events.eventCompletionEvents[id]
export let selectCompletionEventsStatusByID =
  (id: string) => (state: AppRootState) =>
    state.events.eventCompletionEventsStatus[id]

export let selectEventCompliancesByID = (id: string) => (state: AppRootState) =>
  state.events.eventCompliances[id]
export let selectEventCompliancesStatus = (state: AppRootState) =>
  state.events.eventCompliancesStatus

export let selectCategories = (state: AppRootState) => state.events.categories
export let selectCategoriesStatus = (state: AppRootState) =>
  state.events.categoriesStatus

export let selectUsersByLinkedService = (id: string) => (state: AppRootState) =>
  state.events.users[id]
export let selectUsersStatusByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.events.usersStatus[id]
export let selectUsersByEvent = (id: string) => (state: AppRootState) =>
  state.events.users[id]
export let selectUsersStatusByEvent = (id: string) => (state: AppRootState) =>
  state.events.usersStatus[id]

export let selectUserGroupsByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.events.userGroups[id]
export let selectUserGroupsStatusByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.events.userGroupsStatus[id]

export let selectLinkedServices = (state: AppRootState) =>
  state.events.linkedServices
export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.events.linkedServicesStatus

export let selectQuizByEvent = (eventID: string) => (state: AppRootState) =>
  state.events.quizzes[eventID]
export let selectQuizStatusByEvent =
  (eventID: string) => (state: AppRootState) =>
    state.events.quizzesStatus[eventID]

export let selectQuestionsByQuiz = (quizID: string) => (state: AppRootState) =>
  state.events.quizQuestions[quizID]
export let selectQuestionsStatusByQuiz =
  (quizID: string) => (state: AppRootState) =>
    state.events.quizQuestionsStatus[quizID]

export let selectSessionQuestionsByQuiz =
  (quizID: string) => (state: AppRootState) =>
    state.events.quizSessionQuestions[quizID]
export let selectSessionQuestionsStatusByQuiz =
  (quizID: string) => (state: AppRootState) =>
    state.events.quizSessionQuestionsStatus[quizID]

export let selectQuizSessionAnswersByQuiz =
  (quizID: string) => (state: AppRootState) =>
    state.events.quizSessionAnswers[quizID]
export let selectQuizSessionAnswersStatusByQuiz =
  (quizID: string) => (state: AppRootState) =>
    state.events.quizSessionAnswersStatus[quizID]

export let selectQuizSessionResults =
  (eventID: string) => (state: AppRootState) =>
    state.events.quizSessionResults[eventID]
export let selectQuizSessionResultsStatus =
  (eventID: string) => (state: AppRootState) =>
    state.events.quizSessionResultsStatus[eventID]

export let selectEventCERecords = (eventID: string) => (state: AppRootState) =>
  state.events.eventCERecords[eventID]
export let selectEventCERecordsStatus =
  (eventID: string) => (state: AppRootState) =>
    state.events.eventCERecordsStatus[eventID]

export let selectEventTemplates = (state: AppRootState) =>
  state.events.templates
export let selectEventTemplatesStatus = (state: AppRootState) =>
  state.events.templatesStatus

export let selectEventTemplateAssociations = (state: AppRootState) =>
  state.events.templateAssociations
export let selectEventTemplateAssociationsStatus = (state: AppRootState) =>
  state.events.templateAssociationsStatus
export let selectTemplateByEventId =
  (eventId: string) => (state: AppRootState) => {
    let association = state.events.templateAssociations.find(
      (a) => a.eventID === eventId,
    )
    if (!association) return null
    return (
      state.events.templates.find((t) => t.id === association?.templateID) ||
      null
    )
  }

export let selectSyllabusByEventID = (id: string) => (state: AppRootState) =>
  state.events.syllabi[id]
export let selectSyllabusStatusByEventID =
  (id: string) => (state: AppRootState) =>
    state.events.syllabiStatus[id]

export let selectReferences = (state: AppRootState) => state.events.references
export let selectReferencesStatus = (state: AppRootState) =>
  state.events.referencesStatus

export default eventsSlice.reducer
